import React, { useEffect } from "react";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";

const Contact = () => {
  const { webinfo } = useLocalContext();
  const brand = webinfo?.name || "Our Support Team";

  useEffect(() => {
    document.title = `Contact | ${brand}`;
  }, [brand]);

  const cleanPhone = (webinfo.phonecall || "").replace(/\D/g, "");
  const waHref = cleanPhone ? `https://wa.me/${cleanPhone}` : "#";

  return (
    <section className="nc" aria-labelledby="nc-heading">
      {/* Header */}
      <header className="nc-head">
        <span className="nc-tag">Support & enquiries</span>
        <h1 id="nc-heading">
          Need help with your<span>security plan or activation?</span>
        </h1>
        <p>
          The {brand} team is here to help you understand plans, complete
          activation, and figure out next steps when something isn’t working
          as expected.
        </p>

        <div className="nc-quick" aria-label="Quick contact options">
          {webinfo.phonecall && (
            <a className="nc-chip" href={`tel:${webinfo.phonecall}`}>
              📞 Call us: {webinfo.phone || webinfo.phonecall}
            </a>
          )}
          {webinfo.email && (
            <a className="nc-chip" href={`mailto:${webinfo.email}`}>
              ✉️ Email: {webinfo.email}
            </a>
          )}
          {waHref !== "#" && (
            <a
              className="nc-chip"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              💬 WhatsApp support
            </a>
          )}
        </div>
      </header>

      {/* Main Grid */}
      <div className="nc-grid">
        {/* LEFT: Info / assistance types */}
        <aside className="nc-panel" aria-label="Support information">
          <h2>When to contact us</h2>
          <p className="nc-lead">
            Reach out whenever you need a human to walk you through something:
            choosing a plan, activating a key, or understanding what your
            security software is telling you.
          </p>

          <ul className="nc-list" role="list">
            {webinfo.phone && (
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || webinfo.phone}`}>
                  {webinfo.phone}
                </a>
              </li>
            )}
            {webinfo.email && (
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email}`}>{webinfo.email}</a>
              </li>
            )}
            {webinfo.address && (
              <li>
                <strong>Service location:</strong> {webinfo.address}
              </li>
            )}
            <li>
              <strong>Support style:</strong> Remote guidance + screen-share
              where appropriate
            </li>
          </ul>

          <div className="nc-hours">
            <h3>Typical response times</h3>
            <p>WhatsApp &amp; calls: usually within business hours.</p>
            <p>Email: generally within one working day.</p>
          </div>

          <div className="nc-badges" aria-label="Reassurance points">
            <span className="nc-badge">Plain-language explanations</span>
            <span className="nc-badge">Help with activation steps</span>
            <span className="nc-badge">Guidance before you buy</span>
          </div>

          <div className="nc-help-types">
            <h3>We can help with</h3>
            <ul>
              <li>Questions about device limits &amp; plan differences</li>
              <li>License key delivery and basic activation issues</li>
              <li>Simple “is this normal?” alerts and notifications</li>
            </ul>
            <p className="nc-small">
              For in-depth technical errors or warranty issues inside the app,
              the official software vendor&apos;s support team remains the
              primary contact.
            </p>
          </div>
        </aside>

        {/* RIGHT: Form */}
        <div className="nc-formpanel" aria-label="Contact form">
          <h2>Send us a support request</h2>
          <p className="nc-formsub">
            Share a few details so we can understand your setup and reply with
            specific next steps.
          </p>

          <form
            className="nc-form"
            onSubmit={(e) => e.preventDefault()}
            noValidate
          >
            <div className="nc-field">
              <label htmlFor="fullName">Full name</label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                placeholder="Your name"
                required
              />
            </div>

            <div className="nc-two">
              <div className="nc-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="nc-field">
                <label htmlFor="phone">Phone number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+91 98xxxxxxx"
                />
              </div>
            </div>

            <div className="nc-two">
              <div className="nc-field">
                <label htmlFor="topic">What do you need help with?</label>
                <select id="topic" name="topic" defaultValue="choose">
                  <option value="choose" disabled>
                    Select a topic
                  </option>
                  <option value="activation">Activation / license key</option>
                  <option value="installation">Installation issue</option>
                  <option value="plan">Choosing a plan</option>
                  <option value="renewal">Renewal / extension</option>
                  <option value="billing">Billing / payment question</option>
                  <option value="other">Something else</option>
                </select>
              </div>

              <div className="nc-field">
                <label htmlFor="devices">How many devices?</label>
                <select id="devices" name="devices" defaultValue="1">
                  <option value="1">1 device</option>
                  <option value="3">Up to 3 devices</option>
                  <option value="5">Up to 5 devices</option>
                  <option value="10">10+ devices</option>
                </select>
              </div>
            </div>

            <div className="nc-field">
              <label htmlFor="message">Describe what’s going on</label>
              <textarea
                id="message"
                rows="6"
                placeholder="Example: I bought a plan for 3 devices but only see 1 active in my account…"
                required
              />
            </div>

            <div className="nc-terms">
              <label className="nc-check">
                <input type="checkbox" required /> I’ve read and agree to the{" "}
                <a href="/privacy-policy">Privacy Policy</a>.
              </label>
            </div>

            <div className="nc-actions">
              <button type="submit" className="nc-btn primary">
                Submit request
              </button>
              {waHref !== "#" && (
                <a
                  className="nc-btn outline"
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Continue on WhatsApp
                </a>
              )}
            </div>

            <p className="nc-note">
              Details you share here are used only to respond to your request.
              For full information, please review our Privacy Policy.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
